import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * One player for the onboarding samples (V3-w): which sample owns it and
 * whether it is playing — the archive's and the feed's player
 * (`useArchivePlayer`, `useFeedPlayer`) without the fetch, because a
 * sample's `audioUrl` is public (`reference/`, V3-b) and the element can
 * take it straight. `error` is `'failed'` when the element could not play
 * it; the page turns that into a sentence and clears it.
 */
export function useSamplePlayer() {
  const audioRef = useRef(null)
  const [playingId, setPlayingId] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [error, setError] = useState(null)

  const element = useCallback(() => {
    if (audioRef.current) return audioRef.current
    const audio = new Audio()
    audio.preload = 'auto'
    audio.addEventListener('play', () => setIsPlaying(true))
    audio.addEventListener('pause', () => setIsPlaying(false))
    audio.addEventListener('ended', () => setIsPlaying(false))
    audio.addEventListener('error', () => {
      setIsPlaying(false)
      setPlayingId(null)
      setError('failed')
    })
    audioRef.current = audio
    return audio
  }, [])

  useEffect(() => {
    return () => {
      audioRef.current?.pause()
    }
  }, [])

  const toggle = useCallback(
    async (id, url) => {
      const audio = element()
      if (playingId === id) {
        if (audio.paused) await audio.play().catch(() => setError('failed'))
        else audio.pause()
        return
      }
      setError(null)
      try {
        audio.pause()
        audio.src = url
        setPlayingId(id)
        await audio.play()
      } catch {
        setPlayingId(null)
        setError('failed')
      }
    },
    [element, playingId],
  )

  const stop = useCallback(() => {
    audioRef.current?.pause()
  }, [])

  const clearError = useCallback(() => setError(null), [])

  return { playingId, isPlaying, error, toggle, stop, clearError }
}
